import { useCallback, useEffect, useRef, useState } from "react";
import type {
  JobActChoiceId,
  PlanInputView,
  PlanVersionSummary,
  ReviewFindingResolution,
  WorkItemGate,
} from "@yagni/shared";
import { captureUiError } from "../sentry.js";

export type ResolveGateInput = {
  workItemId: string;
  gateId: string;
  /**
   * The human-edited proposal field map (editable-proposal substrate, YAG-116).
   * Carried on an APPROVE-shaped resolve when the gate rendered an editable
   * proposal, so the resolve route applies + captures the suggested→final
   * revision (proposal_revisions / FIT) — the SAME body shape the Feed sends
   * (`useFeedItemState.buildRequest`). Absent → an unedited resolve carries none.
   */
  editedProposal?: Record<string, unknown> | null;
} & (
  | {
      action: "accept_proposal";
      scopePatch?: Record<string, unknown> | null;
      startPlanning?: boolean;
    }
  | { action: "decline_proposal" }
  // Approve names the plan version the person was looking at (spec
  // 2026-11-02): the answers are in it already, each saved on its own through
  // `answerPlanQuestion`, so the approve carries none. The route refuses with
  // `plan_version_stale` when the plan moved since.
  | {
      action: "approve_execution_plan";
      planId: string;
      planVersionId?: string;
      expectedPrototypeReferencesHash?: string;
    }
  // Send back's note is queued for the plan like an answer: Reeve edits the
  // plan for it, and the gate stays open on the version that comes back.
  | { action: "request_plan_changes"; feedback: string }
  // A written answer to the agent's prose question. `response.text` is the
  // answer, and the route posts it to the item's record so the next attempt
  // actually reads it. `resume` asks the route to pick the work back up where
  // it can; the response's `queued` and `phase` say what it did.
  | { action: "answer_input"; response: Record<string, unknown>; resume?: boolean }
  // A plain question that is not needed: closes the item (phase dismissed).
  | { action: "dismiss_input" }
  // The two ways out of a paused run (YAG-373). Proceeding appends the step to
  // the plan as a revision the editor authored; stopping halts and sends the
  // plan back. Neither is a free-text reply, which is why neither is
  // `answer_input`.
  | { action: "amend_plan"; amendment: string }
  | { action: "stop_to_planning"; note?: string }
  // A stopped build's one-click ways on. Neither carries what it changes: the
  // route reads the extra attempt and the plan patch off the gate the stop
  // wrote, so a client can never widen the plan past what the stop listed.
  | { action: "continue_anyway" }
  | { action: "patch_plan" }
  | {
      action: "accept_result";
      /**
       * The editor's per-finding keep/edit/remove decisions at a deep-review
       * hold (YAG-372). Carried on the SAME resolve as the verdict and stored
       * in the gate's own resolution; absent on every ordinary result review.
       */
      findings?: ReviewFindingResolution[];
    }
  | { action: "request_result_changes"; feedback: string }
  // A Job's act, answered (ADR-0044, plan Task 6). The only pair keyed off the
  // ACT rather than the gate kind, which is what makes every act answerable
  // including the ones that mint the generic `job_act` gate. The route reads
  // the act off the gate's own payload, so neither half carries it: a client
  // that could name the act could name a different one.
  // `choice` names WHICH ending, on an act that has more than one: a fix round
  // out of rounds asks continue / take over / stop, and one of those is not a
  // decline. The route refuses a choice the act does not offer, so naming one
  // is not a way to reach an act's other verbs.
  | { action: "perform_job_act"; choice?: JobActChoiceId; feedback?: string }
  | { action: "decline_job_act"; feedback?: string }
);

/**
 * What the open plan gate holds beside the gate (spec 2026-11-02,
 * `PlanGateInputsState`): the plan as it stands, its newest version, and
 * every answer, note and reply said at it. `planInputs` is null when the read
 * carried none (any other gate, or the server could not read them), which is
 * not the same as a plan gate with nothing said yet (`[]`).
 */
export interface OpenGatePlanState {
  planVersion: PlanVersionSummary | null;
  planInputs: PlanInputView[] | null;
}

const NO_PLAN_STATE: OpenGatePlanState = { planVersion: null, planInputs: null };

/**
 * `quiet` is a background re-read (the sheet's clock while Reeve edits the
 * plan): no loading state, and a read that fails keeps the gate it has
 * instead of clearing it, so one network blip does not drop the plan gate.
 */
export interface GateReadOptions {
  quiet?: boolean;
}

export function useOpenWorkItemGate(workItemId: string | null): {
  gate: WorkItemGate | null;
  planVersion: PlanVersionSummary | null;
  planInputs: PlanInputView[] | null;
  /** The inputs as an answer's own response returned them, before the next read. */
  setPlanInputs: (inputs: PlanInputView[]) => void;
  isLoading: boolean;
  error: Error | null;
  refetch: (opts?: GateReadOptions) => Promise<void>;
} {
  const [gate, setGate] = useState<WorkItemGate | null>(null);
  const [plan, setPlan] = useState<OpenGatePlanState>(NO_PLAN_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  /** A quiet read failed and none has landed since: reported once per run of failures. */
  const quietFailing = useRef(false);

  const refetch = useCallback(
    async (opts: GateReadOptions = {}) => {
      if (!workItemId) {
        setGate(null);
        setPlan(NO_PLAN_STATE);
        setIsLoading(false);
        setError(null);
        return;
      }
      if (!opts.quiet) {
        setIsLoading(true);
        setError(null);
      }
      try {
        const res = await fetch(`/api/work-items/${workItemId}/gates/open`, {
          credentials: "include",
        });
        if (res.status === 404) {
          const missing = (await res.json().catch(() => null)) as { error?: string } | null;
          if (missing?.error === "Open gate not found") {
            setGate(null);
            setPlan(NO_PLAN_STATE);
            return;
          }
        }
        if (!res.ok) {
          throw new Error(`Gate load failed (${res.status})`);
        }
        const payload = (await res.json()) as
          | WorkItemGate
          | ({ gate?: WorkItemGate | null } & Partial<OpenGatePlanState>)
          | null;
        let nextGate: WorkItemGate | null;
        let nextPlan: OpenGatePlanState = NO_PLAN_STATE;
        if (payload && typeof payload === "object" && "gate" in payload) {
          nextGate = payload.gate ?? null;
          nextPlan = {
            planVersion: payload.planVersion ?? null,
            planInputs: Array.isArray(payload.planInputs) ? payload.planInputs : null,
          };
        } else {
          nextGate = payload as WorkItemGate | null;
        }
        setGate(nextGate);
        setPlan(nextPlan);
        quietFailing.current = false;
        if (opts.quiet) setError(null);
      } catch (err) {
        // A quiet read keeps what the last one read: the next tick reads
        // again. The first failure in a row is reported, as expected.
        if (opts.quiet) {
          if (!quietFailing.current) {
            quietFailing.current = true;
            captureUiError(err, {
              area: "Work",
              operation: "gate_quiet_reread",
              workItemId,
              severity: "warning",
              expected: true,
            });
          }
          return;
        }
        setGate(null);
        setPlan(NO_PLAN_STATE);
        setError(err instanceof Error ? err : new Error("Gate load failed"));
      } finally {
        if (!opts.quiet) setIsLoading(false);
      }
    },
    [workItemId],
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const setPlanInputs = useCallback((inputs: PlanInputView[]) => {
    setPlan((prev) => ({ ...prev, planInputs: inputs }));
  }, []);

  return {
    gate,
    planVersion: plan.planVersion,
    planInputs: plan.planInputs,
    setPlanInputs,
    isLoading,
    error,
    refetch,
  };
}

/**
 * What a sheet says at a plan gate, one at a time (spec 2026-11-02): an
 * answer to one question (or its change), its Undo (`answer: null`), a Retry
 * of an input that did not apply, keeping an answer that did not apply as its
 * decision (`decisionOnly`), or the dismissal of a note that did not.
 */
export type PlanAnswerBody =
  | { questionId: string; answer: string; why?: string; skipped?: boolean }
  | { questionId: string; answer: null }
  | { inputId: string; retry: true }
  | { inputId: string; decisionOnly: true }
  | { inputId: string; answer: null };

/**
 * Save it on the server: `POST /api/work-items/:id/gates/:gateId/answers`.
 * The input is queued for the plan and Reeve's edit starts; the response is
 * every input on the gate as it now stands. A refusal throws a
 * `GateResolveError` carrying the route's body (`plan_question_gone`,
 * `plan_decided`).
 */
export async function answerPlanQuestion(
  workItemId: string,
  gateId: string,
  body: PlanAnswerBody,
): Promise<{ inputs: PlanInputView[] }> {
  const res = await fetch(
    `/api/work-items/${encodeURIComponent(workItemId)}/gates/${encodeURIComponent(gateId)}/answers`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  const payload = (await res.json().catch(() => null)) as {
    error?: string;
    inputs?: PlanInputView[];
  } | null;
  if (!res.ok) {
    throw new GateResolveError(
      payload?.error ?? `Saving the answer failed (${res.status})`,
      res.status,
      payload,
    );
  }
  return { inputs: Array.isArray(payload?.inputs) ? payload.inputs : [] };
}

/** What the resolve route reports back. Only held PR-review confirms carry
 * `reviewDelivery`; every other gate resolve answers with the gate alone. */
export interface ResolveGateResponse {
  planning?: { status: "started" | "not_started"; reason?: string };
  reviewDelivery?: {
    posted: boolean;
    reason?: string;
    /** Posted, but the draft stays a draft: its build did not pass everything. */
    heldInDraft?: string;
  } | null;
  /** A Job act's real outcome: whether the act landed, and what it did. */
  dispatched?: boolean;
  detail?: string;
  /** An answer: whether work was scheduled, and the phase the item landed in. */
  queued?: boolean;
  /** How a stopped build went on, when the answer continued it (`budget_raised`, `run_again`). */
  continued?: string;
  answerPosted?: boolean;
  phase?: string;
}

/** A refused resolve, with the route's whole body (a `choose_repo` carries `repos`). */
export class GateResolveError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "GateResolveError";
    this.status = status;
    this.body = body;
  }
}

export function useResolveWorkItemGate(): {
  resolveGate: (input: ResolveGateInput) => Promise<ResolveGateResponse | null>;
  isResolving: boolean;
  error: Error | null;
} {
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const resolveGate = useCallback(async (input: ResolveGateInput) => {
    const { workItemId, gateId, editedProposal, ...rest } = input;
    // Mirror the Feed's body shape (buildRequest): carry `editedProposal` only
    // when it's a real field map — an unedited resolve sends no such key.
    const body: Record<string, unknown> =
      editedProposal && Object.keys(editedProposal).length > 0
        ? { ...rest, editedProposal }
        : { ...rest };
    setIsResolving(true);
    setError(null);
    try {
      const res = await fetch(`/api/work-items/${workItemId}/gates/${gateId}/resolve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new GateResolveError(
          payload.error ?? `Gate resolve failed (${res.status})`,
          res.status,
          payload,
        );
      }
      return (await res.json().catch(() => null)) as ResolveGateResponse | null;
    } catch (err) {
      const nextError = err instanceof Error ? err : new Error("Gate resolve failed");
      setError(nextError);
      throw nextError;
    } finally {
      setIsResolving(false);
    }
  }, []);

  return { resolveGate, isResolving, error };
}

/**
 * "Advance now" (WS2): re-arm a parked Play step so its next leg runs on the
 * Team's next pass. POSTs to the re-arm endpoint (which only schedules
 * `due_at = now`; it does NOT start a coding run), then the caller surfaces a
 * transient "Queued" status and refreshes; it never claims the item is
 * "running now". Only Play steps offer it. Any other item gets a 409 with
 * `code: "no_consumer"`, and the server's sentence is surfaced as the error.
 * Modeled on `useResolveWorkItemGate`.
 */
export function useAdvanceWorkItem(): {
  advance: (workItemId: string) => Promise<void>;
  isAdvancing: boolean;
  error: Error | null;
} {
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const advance = useCallback(async (workItemId: string) => {
    setIsAdvancing(true);
    setError(null);
    try {
      const res = await fetch(`/api/work-items/${workItemId}/advance`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(payload.error ?? `Advance failed (${res.status})`);
      }
      await res.json().catch(() => null);
    } catch (err) {
      const nextError = err instanceof Error ? err : new Error("Advance failed");
      setError(nextError);
      throw nextError;
    } finally {
      setIsAdvancing(false);
    }
  }, []);

  return { advance, isAdvancing, error };
}
